import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './HomePageV5.css';
import './StorytellingLandingTemplate.css';
import { constructDownloadUrl } from './LeadMagnetLandingPage';
import { trackLinkedInConversion, LINKEDIN_CONVERSIONS } from '../../services/linkedin';
import { trackGoogleAdsConversion, GOOGLE_ADS_CONVERSIONS } from '../../services/googleAds';

// The 7 keys are an internal authoring framework only; their names never
// reach the reader. The customer-facing label is the optional `title` the
// admin types per slot. Order is fixed and matches the storytelling beat.
const SLOT_ORDER = ['problem', 'enemy', 'stakes', 'insight', 'solution', 'proof', 'action'];

/**
 * Tolerate every shape a sound-byte slot may carry:
 *   - new:        { title: string | null, body: string, link?: { caption, url } }
 *   - intermediate: { title: string | null, body: string }
 *   - legacy:     "plain body string"
 * The optional `link` is currently authored only on the Action slot, but the
 * renderer is generic so any slot with a non-empty link will render it.
 * Returns null for empty/blank slots so the caller can skip rendering.
 */
function readSection(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    const body = value.trim();
    return body ? { title: null, body, link: null } : null;
  }
  if (typeof value === 'object') {
    const title = (value.title || '').trim() || null;
    const body = (value.body || '').trim();
    if (!body) return null;
    let link = null;
    if (value.link && typeof value.link === 'object') {
      const caption = (value.link.caption || '').trim();
      const url = (value.link.url || '').trim();
      if (caption && url) link = { caption, url };
    }
    return { title, body, link };
  }
  return null;
}

/**
 * Parse a YouTube or Vimeo URL into an embed URL. Returns null if the URL is
 * unrecognized so callers can fall back to a raw link.
 */
function toEmbedUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  const url = rawUrl.trim();
  if (!url) return null;

  // youtu.be/<id>
  const youtuBe = url.match(/youtu\.be\/([\w-]{6,})/);
  if (youtuBe) {
    return `https://www.youtube.com/embed/${youtuBe[1]}`;
  }

  // youtube.com/watch?v=<id> or youtube.com/embed/<id> or youtube.com/shorts/<id>
  const youtube = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  if (youtube) {
    return `https://www.youtube.com/embed/${youtube[1]}`;
  }

  // vimeo.com/<id>
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) {
    return `https://player.vimeo.com/video/${vimeo[1]}`;
  }

  return null;
}

function CtaBoxView({ ctaBox }) {
  const [downloadTriggered, setDownloadTriggered] = useState(false);
  const [downloadComplete, setDownloadComplete] = useState(false);

  if (!ctaBox || !ctaBox.type) return null;

  const title = ctaBox.title || '';
  const description = ctaBox.description || '';
  const buttonText = ctaBox.button_text || ctaBox.buttonText || 'Get Started';

  const handleDownload = (url) => {
    if (!url || url === '#') return;
    const link = document.createElement('a');
    link.href = url;
    link.download = '';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setDownloadTriggered(true);
    setTimeout(() => setDownloadComplete(true), 1500);
    try {
      trackLinkedInConversion(LINKEDIN_CONVERSIONS.DOWNLOAD_COMPLETE);
    } catch (err) {
      // Tracking is best-effort; never block the download UX
    }
    try {
      trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.DOWNLOAD_COMPLETE);
    } catch (err) {
      // Tracking is best-effort; never block the download UX
    }
  };

  if (ctaBox.type === 'VIDEO') {
    const videoUrl = ctaBox.video_url || ctaBox.videoUrl;
    const embedUrl = toEmbedUrl(videoUrl);
    return (
      <aside className="story-cta-box" aria-label="Featured video">
        <div className="story-cta-video">
          {embedUrl ? (
            <iframe
              src={embedUrl}
              title={title || 'Featured video'}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : videoUrl ? (
            <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="story-cta-video-fallback">
              Watch the video
            </a>
          ) : (
            <div className="story-cta-video-placeholder">No video URL configured</div>
          )}
        </div>
        {(title || description) && (
          <div className="story-cta-text">
            {title && <h3 className="story-cta-title">{title}</h3>}
            {description && <p className="story-cta-description">{description}</p>}
          </div>
        )}
      </aside>
    );
  }

  if (ctaBox.type === 'DOWNLOAD') {
    const assetId = ctaBox.asset_id || ctaBox.assetId;
    const downloadUrl = constructDownloadUrl(assetId);
    return (
      <aside className="story-cta-box" aria-label="Download resource">
        <div className="story-cta-text">
          {title && <h3 className="story-cta-title">{title}</h3>}
          {description && <p className="story-cta-description">{description}</p>}
        </div>
        {downloadUrl && downloadUrl !== '#' ? (
          <>
            {!downloadTriggered && (
              <a
                href={downloadUrl}
                className="story-cta-button"
                download
                onClick={(e) => { e.preventDefault(); handleDownload(downloadUrl); }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M10 2v12M6 10l4 4 4-4M2 16h16" />
                </svg>
                {buttonText}
              </a>
            )}
            {downloadTriggered && !downloadComplete && (
              <div className="story-cta-status story-cta-status--pending">Downloading...</div>
            )}
            {downloadComplete && (
              <div className="story-cta-status story-cta-status--success">Download started</div>
            )}
          </>
        ) : (
          <div className="story-cta-status story-cta-status--pending">No file attached</div>
        )}
      </aside>
    );
  }

  if (ctaBox.type === 'LINK') {
    const externalUrl = ctaBox.external_url || ctaBox.externalUrl;
    return (
      <aside className="story-cta-box" aria-label="Call to action">
        <div className="story-cta-text">
          {title && <h3 className="story-cta-title">{title}</h3>}
          {description && <p className="story-cta-description">{description}</p>}
        </div>
        {externalUrl ? (
          <a
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="story-cta-button"
          >
            {buttonText}
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M7 13L17 3M17 3H9M17 3v8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        ) : (
          <div className="story-cta-status story-cta-status--pending">No URL configured</div>
        )}
      </aside>
    );
  }

  return null;
}

function StorytellingLandingTemplate({ leadMagnet }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const soundBytes = leadMagnet.soundBytes || {};
  const funnelLinks = Array.isArray(leadMagnet.funnelLinks) ? leadMagnet.funnelLinks : [];
  const ctaBox = leadMagnet.ctaBox || {};

  return (
    <div className="story-page">
      {/*
        Reuse the home page (HomePageV5) navigation. The nav is styled for a
        dark hero background; on this white page we always render it in its
        "scrolled" state so the dark backdrop keeps the logo and links readable.
      */}
      <nav className="hp5-nav hp5-nav--scrolled">
        <div className="hp5-nav__inner">
          <button
            className="hp5-nav__burger"
            aria-label="Menu"
            onClick={() => setMenuOpen(o => !o)}
          >
            <span /><span /><span />
          </button>
          <a href="/" className="hp5-nav__logo" aria-label="CerebralMachines home">
            <img src="/CMLogo.png" alt="CerebralMachines" />
          </a>
          <div className="hp5-nav__links">
            <a href="/">Home</a>
            <a href="/platform">Platform</a>
            <a href="/how-it-works">How it Works</a>
            <a href="/use-cases">Use Cases</a>
          </div>
          <div className="hp5-nav__actions">
            <button className="hp5-btn--ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="hp5-btn--primary" onClick={() => navigate('/request-demo')}>Request a Demo</button>
          </div>
        </div>
        {menuOpen && (
          <div className="hp5-nav__mobile-menu">
            <a
              href="/login"
              onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/login'); }}
            >
              Sign In
            </a>
            <a
              href="/request-demo"
              onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/request-demo'); }}
            >
              Request a Demo
            </a>
          </div>
        )}
      </nav>

      <main className="story-container">
        <section className="story-hero">
          {leadMagnet.pageTitle && (
            <p className="story-eyebrow">{leadMagnet.pageTitle}</p>
          )}
          {leadMagnet.mainHeadline && (
            <h1 className="story-headline">{leadMagnet.mainHeadline}</h1>
          )}
          {leadMagnet.subHeadline && (
            <p className="story-subheadline">{leadMagnet.subHeadline}</p>
          )}
        </section>

        <div className="story-body">
          <div className="story-cta-mobile-anchor">
            <CtaBoxView ctaBox={ctaBox} />
          </div>

          <div className="story-content">
            <article className="story-article">
              {SLOT_ORDER.map((key) => {
                const section = readSection(soundBytes[key]);
                if (!section) return null;
                return (
                  <section key={key} className="story-section">
                    {section.title && (
                      <h2 className="story-section-title">{section.title}</h2>
                    )}
                    <p className="story-section-body">{section.body}</p>
                    {section.link && (
                      <a
                        href={section.link.url}
                        target={section.link.url.startsWith('http') ? '_blank' : undefined}
                        rel={section.link.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="story-section-cta"
                      >
                        {section.link.caption}
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 20 20"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <path
                            d="M7 13L17 3M17 3H9M17 3v8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </a>
                    )}
                  </section>
                );
              })}
            </article>

            {funnelLinks.length > 0 && (
              <section className="story-links" aria-label="Additional resources">
                <h2 className="story-links-title">Go Deeper</h2>
                <ul className="story-links-list">
                  {funnelLinks.map((link, index) => {
                    if (!link || !link.url) return null;
                    return (
                      <li key={index} className="story-links-item">
                        <a
                          href={link.url}
                          target={link.url.startsWith('http') ? '_blank' : undefined}
                          rel={link.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="story-links-anchor"
                        >
                          {link.text || link.url}
                          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M7 13L17 3M17 3H9M17 3v8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}
          </div>

          <div className="story-cta-desktop-anchor">
            <CtaBoxView ctaBox={ctaBox} />
          </div>
        </div>
      </main>

      <footer className="story-footer">
        <p>&copy; {new Date().getFullYear()} CerebralMachines. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default StorytellingLandingTemplate;
